/**
 * The tick-list of design sections, shared by the export and import dialogs so
 * the admin reads the same rows, in the same order, on both sides of a round
 * trip. Export greys out what this game has nothing for; import simply never
 * passes a row the file doesn't carry.
 */

import {
  TRL_BUNDLE_SECTION_LABELS,
  TRL_BUNDLE_SECTION_HINTS,
  type TrlBundleSection,
} from '@swtorbit/shared-socket-events';

export interface TrlBundleChecklistRow {
  id: TrlBundleSection;
  /** "12 planets · 15 paths" — what this section actually holds. */
  summary: string;
  /** False greys the row out and ignores clicks (nothing to export). */
  available: boolean;
}

interface TrlBundleChecklistProps {
  rows: TrlBundleChecklistRow[];
  selected: ReadonlySet<TrlBundleSection>;
  onToggle: (id: TrlBundleSection) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  disabled?: boolean;
}

export function TrlBundleChecklist({
  rows,
  selected,
  onToggle,
  onSelectAll,
  onSelectNone,
  disabled = false,
}: TrlBundleChecklistProps) {
  const selectableCount = rows.filter((r) => r.available).length;

  return (
    <div className="d-flex flex-column gap-xs">
      <div className="d-flex align-items-center gap-xs">
        <span className="fs-xs text-slate flex-1">
          {selected.size} of {selectableCount} selected
        </span>
        <button
          type="button"
          className="btn btn--outline btn--slate btn--sm"
          onClick={onSelectAll}
          disabled={disabled || selected.size === selectableCount}
        >
          All
        </button>
        <button
          type="button"
          className="btn btn--outline btn--slate btn--sm"
          onClick={onSelectNone}
          disabled={disabled || selected.size === 0}
        >
          None
        </button>
      </div>

      {rows.map((row) => (
        <label
          key={row.id}
          className={`d-flex align-items-start gap-xs p-xs rounded-xs border-md border-space ${
            row.available ? '' : 'o-50'
          }`}
        >
          <input
            className="checkbox"
            type="checkbox"
            checked={selected.has(row.id)}
            disabled={disabled || !row.available}
            onChange={() => onToggle(row.id)}
          />
          <span className="d-flex flex-column gap-0 flex-1">
            <span className="fs-sm fw-bold">{TRL_BUNDLE_SECTION_LABELS[row.id]}</span>
            <span className="fs-xs text-slate">{TRL_BUNDLE_SECTION_HINTS[row.id]}</span>
            <span className="fs-xs text-aqua-dark">
              {row.available ? row.summary : 'Nothing authored in this game'}
            </span>
          </span>
        </label>
      ))}
    </div>
  );
}
